import { TranslateService } from '@ngx-translate/core';
import { Injectable, TemplateRef } from '@angular/core';
import { Subject } from 'rxjs';
import { UploadJob } from '../models/upload-job';

export type ToastMessageConfig = {
    classname: string;
    delay: number;
    autohide?: boolean;
};

export interface Toast extends ToastMessageConfig {
    textOrTpl: string | TemplateRef<unknown>;
}

export interface UploadCompletionToastResult {
    level: 'success' | 'warning';
    message: string;
}

@Injectable({ providedIn: 'root' })
export class ToastMessageService {
    private readonly CONFIG_ERROR_MSG: ToastMessageConfig = {
        classname: 'alert alert-danger',
        delay: 5000,
    };
    private readonly CONFIG_SUCCESS_MSG: ToastMessageConfig = {
        classname: 'alert alert-success',
        delay: 5000,
    };

    private readonly CONFIG_WARNING_MSG: ToastMessageConfig = {
        classname: 'alert alert-warning',
        delay: 8000,
    };

    toasts: Toast[] = [];
    toastRemoved$ = new Subject<Toast>();

    constructor(private translateService: TranslateService) {}

    /**
     * Creates an upload completion toast message based on the job status.
     * @param job The upload job
     * @returns The toast level and translated message
     */
    getUploadCompletionToast(job: UploadJob): UploadCompletionToastResult {
        const hasErrors = this.hasUploadErrors(job);
        const message = this.getUploadCompletionMessage(job, hasErrors);
        return {
            level: hasErrors ? 'warning' : 'success',
            message,
        };
    }

    private hasUploadErrors(job: UploadJob): boolean {
        return (
            job.invalidFormatCount +
                job.fileTooLargeCount +
                job.uploadFailedCount >
            0
        );
    }

    private getUploadCompletionMessage(
        job: UploadJob,
        hasErrors: boolean
    ): string {
        if (job.target.type === 'training') {
            return this.translateService.instant(
                hasErrors
                    ? 'imgDb.upload.toast.warning.training'
                    : 'imgDb.upload.toast.success.training',
                {
                    projectName: job.target.containerName,
                    trainingName: job.target.trainingName,
                }
            );
        }
        return this.translateService.instant(
            hasErrors
                ? 'imgDb.upload.toast.warning.imageDb'
                : 'imgDb.upload.toast.success.imageDb',
            {
                imageDbName: job.target.imageDbName,
            }
        );
    }

    showSuccessMsg(textOrTpl: string | TemplateRef<any>, options?: any) {
        return this.showMsg(this.CONFIG_SUCCESS_MSG, textOrTpl, options);
    }

    showTranslatedSuccessMsg(
        message: string,
        interpolateParams: Object,
        options?: any
    ) {
        const translatedMessage = this.translateMessage(
            message,
            interpolateParams
        );
        return this.showSuccessMsg(translatedMessage, options);
    }

    showErrorMsg(textOrTpl: string | TemplateRef<any>, options?: any) {
        return this.showMsg(this.CONFIG_ERROR_MSG, textOrTpl, options);
    }

    showTranslatedErrorMsg(
        message: string,
        interpolateParams: Object,
        options?: any
    ) {
        const translatedMessage = this.translateMessage(
            message,
            interpolateParams
        );
        return this.showErrorMsg(translatedMessage, options);
    }

    showWarningMsg(textOrTpl: string | TemplateRef<any>, options?: any) {
        return this.showMsg(this.CONFIG_WARNING_MSG, textOrTpl, options);
    }

    showTranslatedWarningMsg(
        message: string,
        interpolateParams: Object,
        options?: any
    ) {
        const translatedMessage = this.translateMessage(
            message,
            interpolateParams
        );
        return this.showWarningMsg(translatedMessage, options);
    }

    showPersistentSuccessMsg(
        textOrTpl: string | TemplateRef<any>,
        options?: any
    ) {
        return this.showMsg(
            { ...this.CONFIG_SUCCESS_MSG, autohide: false },
            textOrTpl,
            options
        );
    }

    showPersistentWarningMsg(
        textOrTpl: string | TemplateRef<any>,
        options?: any
    ) {
        return this.showMsg(
            { ...this.CONFIG_WARNING_MSG, autohide: false },
            textOrTpl,
            options
        );
    }

    private translateMessage(message: string, interpolateParams: Object) {
        return this.translateService.instant(message, interpolateParams);
    }

    private showMsg(
        messageConfig: ToastMessageConfig,
        textOrTpl: string | TemplateRef<any>,
        options?: any
    ): Toast {
        const finalOptions = options
            ? this.mergeOptions(messageConfig, options)
            : messageConfig;
        const toast: Toast = {
            textOrTpl,
            autohide: finalOptions.autohide ?? true,
            ...finalOptions,
        };
        this.toasts.push(toast);
        return toast;
    }

    private mergeOptions(defaultOpt: ToastMessageConfig, opt: any) {
        return { ...defaultOpt, ...opt };
    }

    remove(toast: Toast) {
        this.toasts = this.toasts.filter((t) => t !== toast);
        this.toastRemoved$.next(toast);
    }

    clear() {
        this.toasts.splice(0, this.toasts.length);
    }
}
